import { createContext, useContext, useEffect, useMemo, useState } from 'react'

export type AppLocale = 'pl' | 'cs'

type LocaleContextValue = {
  locale: AppLocale
  setLocale: (next: AppLocale) => void
  /** BCP47, e.g. `pl-PL`, `cs-CZ` */
  localeTag: string
  /** OpenGraph locale, e.g. `pl_PL`, `cs_CZ` */
  ogLocale: string
}

const STORAGE_KEY = 'pds_locale'

const LocaleContext = createContext<LocaleContextValue | null>(null)

function normalizeLocale(raw: unknown): AppLocale {
  return raw === 'cs' ? 'cs' : 'pl'
}

function readStoredLocale(): AppLocale {
  try {
    return normalizeLocale(localStorage.getItem(STORAGE_KEY))
  } catch {
    return 'pl'
  }
}

function getLocaleTag(locale: AppLocale): string {
  return locale === 'cs' ? 'cs-CZ' : 'pl-PL'
}

function getOgLocale(locale: AppLocale): string {
  return locale === 'cs' ? 'cs_CZ' : 'pl_PL'
}

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<AppLocale>(() => readStoredLocale())

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, locale)
    } catch {
      // ignore
    }
    try {
      document.documentElement.lang = locale
    } catch {
      // ignore
    }
  }, [locale])

  const value = useMemo<LocaleContextValue>(() => {
    return {
      locale,
      localeTag: getLocaleTag(locale),
      ogLocale: getOgLocale(locale),
      setLocale: (next) => setLocaleState(normalizeLocale(next)),
    }
  }, [locale])

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext)
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider')
  return ctx
}

